'use client';

import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { ErrorFallback } from '@core/Components/ErrorFallback/ErrorFallback';

import type { LeadFormProps } from '../../Entities/LeadForm';

import styles from './LeadForm.module.css';

const LoadingModal = dynamic(() =>
  import('./StatusDisplays/LoadingModal').then(mod => mod.LoadingModal),
);

const SuccessModal = dynamic(() =>
  import('./StatusDisplays/SuccessModal').then(mod => mod.SuccessModal),
);

const ErrorModal = dynamic(() => import('./StatusDisplays/ErrorModal').then(mod => mod.ErrorModal));

const MessageDealerLeadForm = dynamic(() =>
  import('./LeadForms/MessageDealerLeadForm').then(mod => mod.MessageDealerLeadForm),
);

const MessageDealerFinanceLeadForm = dynamic(() =>
  import('./LeadForms/MessageDealerFinanceLeadForm').then(mod => mod.MessageDealerFinanceLeadForm),
);

const RequestCallbackLeadForm = dynamic(() =>
  import('./LeadForms/RequestCallbackLeadForm').then(mod => mod.RequestCallbackLeadForm),
);

const RequestVideoLeadForm = dynamic(() =>
  import('./LeadForms/RequestVideoLeadForm').then(mod => mod.RequestVideoLeadForm),
);

const RequestImageLeadForm = dynamic(() =>
  import('./LeadForms/RequestImageLeadForm').then(mod => mod.RequestImageLeadForm),
);

const HistoryCheckLeadForm = dynamic(() =>
  import('./LeadForms/HistoryCheckLeadForm').then(mod => mod.HistoryCheckLeadForm),
);

const LeasingOnlineLeadForm = dynamic(() =>
  import('./LeadForms/LeasingOnlineLeadForm').then(mod => mod.LeasingOnlineLeadForm),
);

const BookAppointmentLeadForm = dynamic(() =>
  import('./LeadForms/BookAppointmentLeadForm').then(mod => mod.BookAppointmentLeadForm),
);

const TestDriveLeadForm = dynamic(() =>
  import('./LeadForms/TestDriveLeadForm/TestDriveLeadForm').then(mod => mod.TestDriveLeadForm),
);

export const LeadFormModals = ({ strings, locale }: LeadFormProps) => {
  const activeLeadForm = useAtomValue(activeLeadFormAtom);

  const [openedLeadForms, setOpenedLeadForms] = useState<string[]>([]);

  const pathname = usePathname();

  useEffect(() => {
    setOpenedLeadForms([]);
  }, [pathname]);

  useEffect(() => {
    if (activeLeadForm && !openedLeadForms.includes(activeLeadForm)) {
      setOpenedLeadForms(prev => [...prev, activeLeadForm]);
    }
  }, [activeLeadForm, openedLeadForms]);

  const leadFormComponents: Record<string, React.ComponentType<any>> = {
    'loading-modal': LoadingModal,
    'success-modal': SuccessModal,
    'error-modal': ErrorModal,
    'message-dealer': MessageDealerLeadForm,
    'message-dealer-finance': MessageDealerFinanceLeadForm,
    'request-callback': RequestCallbackLeadForm,
    'request-video': RequestVideoLeadForm,
    'request-images': RequestImageLeadForm,
    'history-check': HistoryCheckLeadForm,
    'leasing-online': LeasingOnlineLeadForm,
    'book-appointment': BookAppointmentLeadForm,
    'driftrock-exclusive': TestDriveLeadForm,
  };

  return (
    <div>
      <div className={styles.overlay} data-visible={activeLeadForm !== null} />
      {openedLeadForms.map(leadFormId => {
        const Component = leadFormComponents[leadFormId];
        if (!Component) return null;
        return (
          <ErrorFallback key={leadFormId}>
            <Component key={leadFormId} strings={strings} locale={locale} data-test-id="modal" />
          </ErrorFallback>
        );
      })}
    </div>
  );
};
